import { useEffect, useMemo, useState } from "react";
import { alpha, Skeleton, useTheme } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useSelector } from "@xstate/react";

import { useCheckoutContext } from "../../../CheckoutMachine";
import { CheckoutPaymentSelectForm } from "../../Checkout/CheckoutForm/components/payment/CheckoutPaymentSelectForm";
import { StripePaymentForm } from "../../Checkout/CheckoutForm/components/payment/StripePaymentForm";
import { convertAssetUrl } from "@/utils/StringUtils";
import { BrandclubStoreConfigUtils } from "@brandclub/common-ui";
import { useAppSelector } from "@/redux/hooks";
import { UserInteractionType } from "@/components/Checkout/types";
import ButtonContainer from "../components/Buttons/ButtonContainer";
import SaveButton from "../components/Buttons/SaveButton";
import ClearButton from "../components/Buttons/ClearButton";

const stripePromise = loadStripe(
  BrandclubStoreConfigUtils.getStripePublishableKey()
);

const Loading = () => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Skeleton
        variant="rectangular"
        height={50}
        sx={{ backgroundColor: "#F6F8FA" }}
      />
      <Skeleton
        variant="rectangular"
        height={50}
        sx={{ backgroundColor: "#F6F8FA" }}
      />
    </div>
  );
};

export const PaymentSelectionForm = ({
  methods,
  clientSecret: clientSecretFromParent,
}: {
  methods?: any[];
  clientSecret?: string;
}) => {
  const { actorRef, send } = useCheckoutContext();

  const order = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
  );
  const products = useSelector(
    actorRef,
    (snapshot) => snapshot.context.retailerCheckoutOrderStatus?.products
  );

  const [isLoading, setIsLoading] = useState(!methods);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [clientSecret, setClientSecret] =
    useState<string | undefined>(undefined);
  const [availablePaymentMethods, setAvailablePaymentMethods] =
    useState<any[] | undefined>(methods);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] =
    useState<string>("");
  const [checkoutIntentId] = useState<string | undefined>(undefined);

  const user = useAppSelector((state) => state.userProfile);
  const theme = useTheme();

  const lineItems = useMemo(() => {
    const items = order?.items || [];
    return items.map((i) => ({
      name: products?.[i.stacklineSku]?.title ?? "",
      amount: Math.round((i.retailPrice ?? 0) * 100),
    }));
  }, [order?.items, products]);

  const shippingRates = useMemo(() => {
    const fees = order?.orderSummary?.shippingFees ?? 0;
    return [
      {
        id: "standard",
        amount: Math.round(fees * 100),
        displayName: "2-5 business days",
        // Stripe types accept string or structured estimate; keep simple here
        deliveryEstimate: "2-5 business days" as any,
      },
    ];
  }, [order?.orderSummary?.shippingFees]);

  const shippingAddressOverride = useMemo(() => {
    const direct = order?.shippingAddress;
    if (direct) {
      return {
        address1: direct.address1,
        address2: direct.address2,
        city: direct.city,
        state: direct.state,
        zipCode: direct.zipCode,
        country: direct.country,
        firstName: direct.firstName,
        lastName: direct.lastName,
        phoneNumber: direct.phoneNumber,
        email: undefined,
      } as any;
    }
    const fromUser = order?.userCheckoutInfo?.shippingAddress;
    if (fromUser) {
      return {
        address1: fromUser.address1,
        address2: fromUser.address2,
        city: fromUser.city,
        state: fromUser.state,
        zipCode: fromUser.zip,
        country: undefined,
        firstName: fromUser.firstName,
        lastName: fromUser.lastName,
        phoneNumber: undefined,
        email: undefined,
      } as any;
    }
    return undefined;
  }, [order]);

  const billingAddressOverride = useMemo(() => {
    const direct = order?.billingAddress;
    if (direct) {
      return {
        address1: direct.address1,
        address2: direct.address2,
        city: direct.city,
        state: direct.state,
        zipCode: direct.zipCode,
        country: direct.country,
        firstName: direct.firstName,
        lastName: direct.lastName,
        phoneNumber: direct.phoneNumber,
        email: undefined,
      } as any;
    }
  }, [order]);

  // If parent passes prefetched data, hydrate local state and skip fetching
  useEffect(() => {
    if (methods) {
      setAvailablePaymentMethods(methods);
      // Do not preselect the previously failed method; force user to choose another
      setSelectedPaymentMethodId("");
      setClientSecret(clientSecretFromParent);
      setShowAddPaymentForm(!methods || methods.length === 0);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods, clientSecretFromParent]);

  // Intentionally no data fetching here; parent provides methods and clientSecret

  if (isLoading || !order) {
    return <Loading />;
  }

  if (error) {
    return <div style={{ color: "#9D3841", fontSize: 14 }}>{error}</div>;
  }

  if (showAddPaymentForm && clientSecret && user) {
    const fontFamily = theme.typography.fontFamily;
    const fontFamilyLink = theme?.settings?.fonts?.fontFamilyLink
      ? convertAssetUrl(theme.settings.fonts.fontFamilyLink)
      : "https://fonts.googleapis.com/css2?family=Poppins";
    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          paymentMethodCreation: "manual",
          fonts: [{ cssSrc: fontFamilyLink }],

          appearance: {
            labels: "floating",
            theme: "stripe",
            variables: {
              fontFamily: fontFamily,
              colorText: theme.vars.palette.text.primary,
              colorTextPlaceholder: alpha(theme.palette.text.primary, 0.5),
              fontSizeSm: "12px",
              borderRadius: "99px",
            },
            rules: {
              ".Input": {
                borderRadius: "8px",
                boxShadow: "none",
                fontSize: "14px",
                border: `1px solid #dcdddc`,
              },
              ".Input:focus": {
                outline: "none",
                border: `1px solid #dcdddc`,
                boxShadow: "none",
              },
              ".Label": {
                color: alpha(theme.palette.text.primary, 0.3),
                fontWeight: "semibold",
              },
              ".Label--resting": {
                fontSize: "16px",
              },
              ".Label--floating": {
                fontSize: "12px",
              },
            },
          },
        }}
      >
        <StripePaymentForm
          onCancel={() => setShowAddPaymentForm(false)}
          onShippingAddressChange={(e) => e.resolve()}
          onShippingRateChange={(e) => e.resolve()}
          lineItems={lineItems}
          shippingRates={shippingRates}
          checkoutIntentIdOverride={checkoutIntentId}
          checkoutIntentOverride={{
            checkoutIntentId,
            shippingAddress: shippingAddressOverride,
            billingAddress: billingAddressOverride,
          }}
          onSuccess={async (_type, paymentMethod) => {
            console.log("onSuccess ===>", paymentMethod);
            try {
              setIsLoading(true);
              setShowAddPaymentForm(false);
              // notify fast machine to continue
              send({
                type: "ON_RETRY_CHECKOUT",
                data: {
                  payment: {
                    paymentMethodId: paymentMethod.id,
                  },
                },
              });
            } finally {
              setIsLoading(false);
            }
          }}
          onError={(e) => setError(e.message)}
        />
      </Elements>
    );
  }

  return (
    <div>
      <CheckoutPaymentSelectForm
        items={availablePaymentMethods || []}
        selectedItem={(availablePaymentMethods || []).find(
          (i) => i.id === selectedPaymentMethodId
        )}
        value={selectedPaymentMethodId}
        disabledIds={
          order?.userCheckoutInfo?.stripePaymentMethod?.id
            ? [order.userCheckoutInfo.stripePaymentMethod.id]
            : []
        }
        onAddNewPaymentMethod={() => setShowAddPaymentForm(true)}
        onChange={async (_event, value) => {
          setIsLoading(true);
          try {
            setSelectedPaymentMethodId(value.id);
            send({
              type: "SUBMIT_USER_INTERACTION",
              data: {
                interactionType: UserInteractionType.STRIPE_PAYMENT_SELECTION,
                interactionData: {
                  paymentMethodId: value.id,
                } as unknown as any,
              },
            });
          } finally {
            setIsLoading(false);
          }
        }}
      />
      <ButtonContainer
        sx={{
          marginTop: "35px",

          alignSelf: "flex-end",
          justifyContent: "space-between",
          [theme.breakpoints.down("md")]: {
            marginTop: "20px",
          },
        }}
      >
        <ClearButton
          sx={{
            [theme.breakpoints.up("md")]: {
              height: "36px !important",
              fontSize: "14px !important",
              padding: "0 16px !important",
            },
          }}
          onClick={() =>
            send({
              type: "ON_SESSION_CLOSE",
              data: { sessionToClose: "payment" },
            })
          }
        >
          Cancel
        </ClearButton>
        <SaveButton
          sx={{
            [theme.breakpoints.up("sm")]: {
              height: "36px !important",
              fontSize: "14px !important",
              padding: "0 16px !important",
            },
          }}
          disabled={!selectedPaymentMethodId}
          onClick={() => {
            send({
              type: "ON_RETRY_CHECKOUT",
              data: {
                payment: {
                  paymentMethodId: selectedPaymentMethodId,
                },
              },
            });
          }}
        >
          Save
        </SaveButton>
      </ButtonContainer>
    </div>
  );
};

export default PaymentSelectionForm;
